// Executive dashboard — domain types.
//
// Exec-only portal over per-client Five9 call-volume + telephony KPIs. Access
// is the unified-permissions service `exec_dashboard` (levels viewer/admin),
// read from restricted.permissions.exec_dashboard.level. There is no legacy
// axis for it — it is granted only through the new permissions system.
//
// The data-model interfaces (TelephonyDaily, PortfolioDaily, Client, config,
// sync-health) are added with the pipeline work (plan phase B). This file
// currently holds just the access role + resolver so Workstream A (the
// permissions foundation) is self-contained.

import { permLevel } from '../permissions/registry';

// ---------- Access role ----------
//
//   viewer → read the dashboard (KPIs, charts, per-client table)
//   admin  → viewer + edit report/alert config + "Sync now"
export type ExecRole = 'viewer' | 'admin';

/**
 * Resolve the caller's exec-dashboard role from their stored restricted map.
 * Returns null if the user has no exec access. Unlike recordings/logs there is
 * NO legacy field to fall back to — exec_dashboard exists only in the unified
 * permissions map.
 */
export function resolveExecRole(
  restricted: { permissions?: Record<string, unknown> } | undefined | null,
): ExecRole | null {
  const v = permLevel(restricted, 'exec_dashboard');
  switch (v) {
    case 'viewer':
    case 'admin':
      return v;
    default:
      return null;
  }
}

/** True when the caller can edit exec-dashboard config / trigger syncs. */
export function isExecAdmin(role: ExecRole | null): boolean {
  return role === 'admin';
}

// ---------- Finance access (Financials silo) ----------
// Stricter than exec_dashboard: dollar figures (revenue, P&L, unit economics)
// render only with this grant. Like exec, it exists only in unified permissions.
export type FinanceRole = 'viewer' | 'admin';

export function resolveFinanceRole(
  restricted: { permissions?: Record<string, unknown> } | undefined | null,
): FinanceRole | null {
  const v = permLevel(restricted, 'finance');
  switch (v) {
    case 'viewer':
    case 'admin':
      return v;
    default:
      return null;
  }
}

// ---------- Client dimension ----------
//
// Canonical client list. There's no centers collection app-side, so this is
// seeded from five9/Campaign Names.txt (best-effort — the file is dirty) and
// auto-maintained by the rollup Lambda on first-seen campaign. A client == a
// Five9 INBOUND campaign name (hyphenated); the outbound campaign is the same
// name + "-Outbound".
export interface Client {
  id: string;                 // canonical inbound campaign name, e.g. "Alta-Centers"
  display_name: string;       // de-hyphenated, e.g. "Alta Centers"
  campaign_inbound: string;   // "Alta-Centers"
  campaign_outbound: string | null; // "Alta-Centers-Outbound" | null
  excluded: boolean;          // system / non-client campaign (Test-Campaign, etc.)
  active: boolean;            // false once soft-off (no calls for N days)
  first_seen_at: number;      // epoch ms
  last_seen_at: number;       // epoch ms
  created_at: number;
  updated_at: number;
}

// ---------- Per-client per-day rollup ----------
//
// Doc id: `${client_id}_${date_key}`. Written ONLY by the rollup Lambda (Admin
// SDK). Later phases (Sheets/Salesforce outcome KPIs, first-time callers) merge
// into the SAME doc via the reserved optional fields below — zero migration.
export interface TelephonyDaily {
  id: string;
  client_id: string;          // canonical inbound campaign name
  date_key: string;           // "yyyymmdd" in reporting tz
  date_start: number;         // epoch ms, midnight(date_key, tz) — range + orderBy field
  direction: 'inbound';       // v1 only; outbound reuses the shape later

  // Volume (aggregate report; cross-checked by detail).
  calls_offered: number;
  answered: number;
  abandoned: number;          // hung up in queue before an agent
  missed: number;             // rang agent(s), no answer

  // Precomputed rates so alerts + default sort don't recompute.
  answer_rate: number;        // answered / offered (0..1)
  abandon_rate: number;       // abandoned / offered (0..1)

  // Timing (seconds).
  asa: number;                // avg speed of answer (answered calls)
  avg_talk_time: number;
  avg_handle_time: number;
  total_talk_time: number;

  // Transfers (detail report).
  warm_transfers_attempted: number;
  warm_transfers_completed: number;

  // Disposition buckets (detail report; AD/NA/HU map).
  admissions_count: number;   // AD
  nonadmissions_count: number;// NA
  hangup_count: number;       // HU

  // Calls by hour-of-day (index 0–23), from detail TIMESTAMP.
  hourly?: number[];

  // Provenance / freshness.
  source: 'aggregate' | 'detail' | 'merged';
  aggregate_synced_at: number | null;
  detail_synced_at: number | null;
  is_final: boolean;          // day closed (date_key < today in tz)
  updated_at: number;
  expires_at: number;         // date_start + 400d

  // Per-disposition counts for this client-day (full disposition name → count).
  // Powers the non-admissions breakdown; omitted when no detail report ran.
  disposition_counts?: Record<string, number>;

  // ---- reserved for later phases (no migration needed) ----
  first_time_callers?: number;               // Phase 2
  insurance_buckets?: Record<string, number>;// Phase 3 (sheets)
  conversions?: number;                      // Phase 3 (Disposition == Admitted)
  verified_admissions?: number;              // Phase 3
  handled_count?: number;                    // Phase 3 (MAH)
  callback_expected_count?: number;          // Phase 3
}

// Compact per-client row embedded in the portfolio doc (the cheap read layer).
export interface PortfolioClientRow {
  client_id: string;
  offered: number;
  answered: number;
  abandoned: number;
  answer_rate: number;
  abandon_rate: number;
  asa: number;
  admissions_count: number;
  warm_transfers_attempted: number;
  warm_transfers_completed: number;
  first_time_callers: number;
  hourly?: number[];
}

// One doc per calendar day: portfolio totals + embedded per-client rows. A
// 30/60/90-day dashboard reads N of these instead of 450×N granular docs.
export interface PortfolioDaily {
  id: string;                 // "yyyymmdd"
  date_key: string;
  date_start: number;         // range + orderBy field
  totals: {
    calls_offered: number;
    answered: number;
    abandoned: number;
    answer_rate: number;
    abandon_rate: number;
    asa: number;
    avg_talk_time: number;
    admissions_count: number;
    warm_transfers_attempted: number;
    warm_transfers_completed: number;
    first_time_callers: number;
    hourly?: number[];
  };
  clients: PortfolioClientRow[];
  // Portfolio-wide per-disposition counts for the day (full name → count).
  // Powers the portfolio non-admissions breakdown without reading granular docs.
  disposition_totals?: Record<string, number>;
  is_final: boolean;
  updated_at: number;
  expires_at: number;
}

// ---------- Intake outcome rollups (Phase 3, from the intake sheets) ----------
// Aggregates only. Written server-side (recordIntakeOutcome + foldIntakeOutcomes).

// Channel from the intake "Serviced By" column. 'all' is the combined view.
export type ChannelKey = 'human' | 'ai' | 'outbound';
export type ChannelFilter = 'all' | ChannelKey;

export interface IntakeCounts {
  intake_count: number;
  admissions_intake: number;
  nonadmissions_intake: number;
  blank_admissions: number;
  callback_expected_count: number;
  handled_count: number;
  conversions: number;
  insurance_buckets: Record<string, number>;
  referral_buckets?: Record<string, number>; // "How did you hear about us" auto-buckets
  first_time_intake?: number;      // Prior Communication = "No"
  first_time_conversions?: number; // Admitted AND Prior Communication = "No"
  repeat_intake?: number;          // Prior Communication = a name / "Yes"
  wt_attempted?: number;           // Transferred To = any attempt
  wt_completed?: number;           // Transferred To = successful
  hourly?: Record<string, number>; // calls by hour-of-day (0–23), map keyed by hour
}
export interface IntakeClientCounts {
  intake_count: number;
  admissions_intake: number;
  nonadmissions_intake: number;
  callback_expected_count: number;
  handled_count: number;   // confirmed-connected (MAH)
  conversions: number;     // Disposition == Admitted
  first_time_intake?: number;
  first_time_conversions?: number;
  repeat_intake?: number;
  wt_attempted?: number;
  wt_completed?: number;
  hourly?: Record<string, number>; // calls by hour-of-day (0–23), map keyed by hour
}
export interface IntakeOutcomeClientRow extends IntakeClientCounts {
  client_id: string;
  // Per-channel breakdown of the per-client metrics (absent on pre-split docs).
  channels?: Record<ChannelKey, IntakeClientCounts>;
}
export interface IntakeOutcomePortfolio {
  id: string;              // yyyymmdd
  date_key: string;
  date_start: number;
  // Combined totals + per-channel breakdown (channels absent on pre-split docs).
  totals: IntakeCounts & { channels?: Record<ChannelKey, IntakeCounts> };
  clients: IntakeOutcomeClientRow[];
  updated_at: number;
  expires_at: number;
}

// ---------- Staffing (from the "Staffing Hours Overview" sheet) ----------
// One doc per month, portfolio-wide (no per-client/per-day granularity in the
// source). Powers calls-per-agent-hour and related productivity metrics.
export interface StaffingMonthly {
  month_key: string;     // "YYYYMM"
  year: number;
  month: number;         // 1–12
  month_name: string;
  month_start: number;   // epoch ms, 1st of month (UTC)
  employee_count: number;
  total_hours: number;
  regular_hours: number;
  overtime_hours: number;
  double_overtime_hours: number;
  pto_hours: number;
  holiday_hours: number;
  unpaid_hours?: number;
  // AGENT-ONLY figures. The CPA workbook's "Employee Hours" tab flags which
  // staff are contact-center agents, so productivity ratios can use agent hours
  // rather than company-wide hours (which include admin/exec and understate it).
  agent_count?: number;
  agent_total_hours?: number;
  agent_regular_hours?: number;
  agent_overtime_hours?: number;
  agent_pto_hours?: number;
  // 'staffing_sheet' = the legacy standalone sheet (now empty).
  // 'cpa_employee_hours' = the CPA workbook's per-employee tab (authoritative).
  source: 'staffing_sheet' | 'cpa_employee_hours';
  updated_at: number;
}

// ---------- Per-client intake sheet links ----------
// Sheet IDs mirrored from DynamoDB (syncClientSheets.js). IDs/URLs only — no PHI;
// the sheets stay Google-access-gated when opened.
export interface ClientSheet {
  client_id: string;
  center_name: string;
  ad_sheet_id: string | null;
  ad_sheet_url: string | null;
  nad_sheet_id: string | null;
  nad_sheet_url: string | null;
  updated_at: number;
}

// ---------- Business silos (Phase A; synced daily by syncDashboardSources) ----------
// Schemas are FIELD-MEANING-based (not sheet-layout-based) so a future
// QuickBooks integration writes the same docs with only `source` changing.

/** One doc per month (id = yyyymm) — revenue, cost, and unit economics. FINANCE-GATED. */
export interface BusinessMonthly {
  month_key: string;          // "yyyymm"
  year: number;
  month: number;              // 1–12
  month_start: number;        // epoch ms UTC (sort field)
  revenue: number;            // services income
  other_income: number;
  payroll: number;            // payroll costs (non-COGS)
  opex: number;               // operating expenses
  cogs: number;
  other_expenses: number;
  total_expenses: number;
  gross_profit: number;       // revenue − COGS
  net_operating_income: number;
  net_income: number;
  billable_calls: number;
  billable_clients: number;
  cost_per_call_cogs: number;
  cost_per_call_loaded: number;
  cost_per_client_payroll: number;
  cost_per_client_loaded: number;
  revenue_per_call: number;
  revenue_per_client: number;
  profit_per_call: number;
  profit_per_client: number;
  labor_cost_pct: number | null;   // 0..1
  agents: number | null;
  employees: number | null;
  revenue_per_employee: number | null;  // annualized (goal $100K)
  cost_per_employee: number | null;
  profit_per_employee: number | null;
  source: 'cpa_sheet' | 'quickbooks';
  updated_at: number;
}

/** Category-level P&L lines for one month (id = yyyymm). FINANCE-GATED. */
export interface PnlMonthly {
  month_key: string;
  year: number;
  month: number;
  month_start: number;
  /** Group label → amount. Labels come from the P&L's "Total for X" rows plus
   *  headline singletons (Gross Profit, Net Operating Income, Net Income). */
  lines: Record<string, number>;
  source: 'cpa_sheet' | 'quickbooks';
  updated_at: number;
}

/** Monthly client churn/adds (id = yyyymm) — from the "Churn Rate" sheet (source of truth). */
export interface ClientChurnMonthly {
  month_key: string;
  year: number;
  month: number;
  month_start: number;
  total_customers: number;
  churn_count: number;
  new_count: number;
  churn_rate: number | null;  // churn_count / total_customers (0..1)
  net_growth: number;         // new − churned
  churned_clients: string[];  // client names (respect the anonymize toggle in UI)
  new_clients: string[];
  paused_clients: string[];
  source: 'churn_sheet';
  updated_at: number;
}

/** Employee turnover for a period (month/quarter/year; id = m_yyyymm | q_yyyyQn | y_yyyy).
 *  cc_* = contact center, co_* = whole company. Rates are fractions (2.44 = 244%). */
export interface TurnoverPeriod {
  id: string;
  period: 'month' | 'quarter' | 'year';
  sort_ms: number;
  year: number;
  month?: number;
  month_key?: string;
  quarter?: number;
  cc_start: number;
  cc_end: number;
  cc_separations: number;
  cc_rate: number | null;
  co_start: number;
  co_end: number;
  co_separations: number;
  co_rate: number | null;
  source: 'cpa_sheet';
  updated_at: number;
}

/** Monthly sales rollup from Close CRM. Money in DOLLARS (Close serves cents).
 *  `won_value` is annualized (monthly deals × 12); `won_value_one_time` is raw. */
export interface SalesMonthly {
  month_key: string;
  year: number;
  month: number;
  month_start: number;
  opps_created: number;
  opps_won: number;
  opps_lost: number;
  won_value: number;
  won_value_one_time: number;
  created_value: number;
  lost_value: number;
  new_leads: number;
  win_rate: number | null;        // won / (won + lost)
  avg_deal_value: number;
  median_days_to_close: number | null;
  source: 'close';
  updated_at: number;
}

/** Current open-pipeline snapshot (doc id 'current'). */
export interface SalesPipelineStage {
  pipeline: string;
  stage: string;
  count: number;
  value: number;                  // annualized $
  median_age_days: number | null;
  oldest_age_days: number | null;
}
export interface SalesPipeline {
  id: string;
  total_open: number;
  total_value: number;
  stages: SalesPipelineStage[];
  /** Lead-status funnel (B2B qualification). Absent on pre-Marketing snapshots. */
  total_leads?: number;
  lead_statuses?: { status: string; count: number }[];
  source: 'close';
  updated_at: number;
}

// ---------- Config / health ----------

export interface TelephonyConfig {
  reporting_timezone: string;   // IANA tz, e.g. "America/Los_Angeles"
  report_folder: string;        // Five9 folder, e.g. "RR Exec Dashboard"
  aggregate_report_name: string;
  detail_report_name: string;
  excluded_campaigns: string[]; // non-client campaigns to skip
  backfill_days: number;        // window for a manual / first pull
  // AD/NA/HU disposition → bucket. Editable so ops can add client dispositions.
  disposition_map: Record<string, 'admissions' | 'nonadmissions' | 'hangup'>;
}

export interface TelephonyAlertsConfig {
  enabled: boolean;
  recipients: string[];         // default ['callvolume@rolloverrep.com']
  min_volume_floor: number;     // skip clients below this avg daily volume
  sustain_days: number;         // consecutive days an anomaly must persist
  cooldown_days: number;        // don't re-alert same client+condition within
  no_calls_days: number;        // zero-calls days → critical
  soft_off_days: number;        // zero for this long → treat as turned off (suppress)
  abandon_rate_ceiling: number; // 0..1
  asa_ceiling_seconds: number;
  mad_multiplier: number;       // robust z threshold (median ± k·MAD)
  min_abs_delta: number;        // absolute call delta gate (dual-gate)
  muted_clients: string[];
}

export interface TelephonySyncState {
  key: string;                  // doc id, e.g. 'rollup'
  last_run_at: number;
  last_date_synced: string | null; // "yyyymmdd"
  rows_scanned: number | null;
  truncated: boolean;
  last_error: string | null;
}

// ---------- UI filter inputs ----------

export type ExecRange = 'thismonth' | 'lastmonth' | '7d' | '30d' | '90d' | 'ytd' | 'custom';

// Resolved [start, end] epoch-ms window that the data layer actually queries.
export interface RangeWindow {
  startMs: number;
  endMs: number;
}

export interface ExecFilters {
  range: ExecRange;
  clients: string[];   // selected client_ids for the chart (empty = portfolio only)
  search: string;      // client-side substring over client name
}
